import { API_URL, Blog_API_TOKEN, ENV, SOURCE_URL} from '../../config';

/**
 * Helper function to fetch article data and its associated SEO MetaImage.
 * Handles preproduction settings and authorization.
 * @param slug The slug of the article to fetch.
 * @param currentLangpart The language part for the URL (e.g., "&locale=zh-Hant-HK" or "").
 * @returns An object containing the article data and SEO data, or nulls if fetching fails.
 */
const fetchArticleAndSEO = async (slug: string, currentLangpart: string) => {
  let url = `${API_URL}/articles?filters[slug][$eq]=${slug}&populate=*${currentLangpart}`;
  let url2 = `${API_URL}/articles?filters[slug][$eq]=${slug}&populate=SEO.MetaImage${currentLangpart}`;

  if (ENV === 'preproduction') {
    url += url.indexOf('?') > -1 ? '&publicationState=preview' : '?publicationState=preview';
    url2 += url2.indexOf('?') > -1 ? '&publicationState=preview' : '?publicationState=preview';
  }

  console.log('Fetching Article URL:', url);
  console.log('Fetching SEO MetaImage URL:', url2);

  const [response, SEOresponse] = await Promise.all([
    fetch(url, { headers: { Authorization: `Bearer ${Blog_API_TOKEN}` } }),
    fetch(url2, { headers: { Authorization: `Bearer ${Blog_API_TOKEN}` } })
  ]);

  if (!response.ok) {
    console.error(`Error fetching Article (main content): ${response.statusText}`);
    return { articleData: null, SEOData: null };
  }

  let articleData = null;
  let SEOData = null;

  try {
    articleData = await response.json();
  } catch (e) {
    console.error('Error parsing main article JSON:', e);
    return { articleData: null, SEOData: null };
  }

  if (SEOresponse.ok) {
    try {
      SEOData = await SEOresponse.json();
    } catch (e) {
      console.error('Error parsing SEO MetaImage JSON:', e);
    }
  } else {
    console.warn(`Warning: Could not fetch SEO MetaImage data. Status: ${SEOresponse.status}, Text: ${SEOresponse.statusText}`);
  }

  // Apply SOURCE_URL for MetaImage if it exists in SEOData and articleData is valid
  if (articleData?.data?.[0]?.attributes && SEOData?.data?.[0]?.attributes?.SEO?.MetaImage?.data?.attributes?.url) {
    articleData.data[0].attributes.SEO = { ...articleData.data[0].attributes.SEO, MetaImage: SOURCE_URL + SEOData.data[0].attributes.SEO.MetaImage.data.attributes.url };
  }
  if(articleData?.data?.[0]){
    if(currentLangpart === '&locale=zh-Hant-HK')
      articleData.data[0].lang = 'zh';
    else
      articleData.data[0].lang = 'en';
  }

  return { articleData, SEOData };
};

export const fetchBlogArticle = async (slug: string, lang: string) => {
  try {
    let langpart = '';
    if (lang.indexOf('zh') > -1) {
      langpart = "&locale=zh-Hant-HK&fallbackLocale=false";
    }

    // First attempt to fetch the article in the requested language
    let { articleData } = await fetchArticleAndSEO(slug, langpart);

    // Fallback logic: If the requested language was English and no article data was found,
    // attempt to fetch the Chinese version.
    if (langpart === '' && (!articleData || articleData.data.length === 0)) {
      console.log('English article not found, attempting to fetch Chinese version as fallback.');
      const chineseLangpart = "&locale=zh-Hant-HK&fallbackLocale=false";
      ({ articleData } = await fetchArticleAndSEO(slug, chineseLangpart));
    }
    // Fallback logic: If the requested language was Chinese and no article data was found,
    // attempt to fetch the English version.
    else if (langpart === '&locale=zh-Hant-HK&fallbackLocale=false' && (!articleData || articleData.data.length === 0)) {
      console.log('Chinese article not found, attempting to fetch English version as fallback.');
      const englishLangpart = "";
      ({ articleData } = await fetchArticleAndSEO(slug, englishLangpart));
    }
    
    console.log('Article API Response:', articleData);

    return articleData;

  } catch (error) {
    console.error('Error fetching Article.', error);
    return null;
  }
};